import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Zap, Plus, ChevronRight, Sparkles, Loader2, Flame, AlertTriangle } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const nav = useNavigate();
  const [scripts, setScripts] = useState([]);
  const [creating, setCreating] = useState(false);
  // Phase 2.1S — Credits burn-rate tile. `null` = still loading, `{}`
  // shape when resolved. Failed fetch stays `null` so the tile stays
  // in the "loading/no-data" skeleton state instead of crashing.
  const [burnRate, setBurnRate] = useState(null);

  useEffect(() => {
    api.get("/scripts").then(({ data }) => setScripts(data)).catch(() => {});
    api.get("/users/me/burn_rate").then(({ data }) => setBurnRate(data)).catch(() => {});
  }, []);

  const openWorkspace = async () => {
    if (creating) return;
    setCreating(true);
    try {
      const { data } = await api.post("/scripts/blank");
      nav(`/workspace/${data.id}`);
    } catch (e) {
      toast.error(t("errors.generic"));
      setCreating(false);
    }
  };

  const modeLabels = {
    indicator: t("dashboard.mode_indicator"),
    strategy: t("dashboard.mode_strategy"),
    alert: t("dashboard.mode_alert"),
    debug: t("dashboard.mode_debug"),
    improve: t("dashboard.mode_improve"),
  };

  return (
    <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-16">
      <div className="mb-12">
        <p className="text-xs font-mono text-blue-400 mb-2">{t("dashboard.eyebrow")}</p>
        <h1 className="text-4xl font-semibold tracking-tighter bg-gradient-to-r from-white to-slate-400 bg-clip-text text-transparent">{t("dashboard.title")}</h1>
        <p className="text-zinc-400 mt-2">{t("dashboard.subtitle")}</p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
        <div className="bg-[#09090B]/90 border border-[#1F2937] backdrop-blur-sm rounded-xl p-6 min-w-0" data-testid="dash-credits-card">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-mono uppercase tracking-wider text-zinc-500 break-words">{t("dashboard.credits_label")}</span>
            <Zap className="w-4 h-4 text-blue-500 flex-shrink-0" />
          </div>
          <div className="text-4xl font-semibold font-mono tracking-tight" data-testid="dash-credits-value">{user?.credits ?? 0}</div>
          <p className="text-xs text-zinc-500 mt-2 break-words leading-relaxed">{t("dashboard.credits_footnote")}</p>
        </div>
        {/* Phase 2.1S — Credits burn-rate tile. Three visual states:
           (1) `days_remaining === null` → "usage stable" (no data yet).
           (2) `is_low_runway === true` → amber pulsing warning + CTA
               to /pricing.
           (3) Otherwise → standard neutral "runway ≈ X days" display.
           The tile lives in the metric grid at grid-cols-4 so it lands
           right next to the credits balance card — the two together
           form the "how much / how long" spend snapshot. */}
        <BurnRateTile data={burnRate} t={t} />
        <div className="bg-[#09090B]/90 border border-[#1F2937] backdrop-blur-sm rounded-xl p-6 min-w-0" data-testid="dash-plan-card">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-mono uppercase tracking-wider text-zinc-500 break-words">{t("dashboard.plan_label")}</span>
          </div>
          <div className="text-4xl font-semibold tracking-tight capitalize">{user?.plan_tier || "free"}</div>
          <Link to="/pricing" className="text-xs text-blue-400 hover:text-blue-300 mt-2 inline-block">{t("dashboard.upgrade_link")}</Link>
        </div>
        <div className="bg-[#09090B]/90 border border-[#1F2937] backdrop-blur-sm rounded-xl p-6 min-w-0" data-testid="dash-scripts-card">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-mono uppercase tracking-wider text-zinc-500 break-words">{t("dashboard.scripts_generated")}</span>
          </div>
          <div className="text-4xl font-semibold font-mono tracking-tight">{scripts.length}</div>
          <p className="text-xs text-zinc-500 mt-2 break-words leading-relaxed">{t("dashboard.total_in_history")}</p>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-6 mb-12">
        <button
          type="button"
          onClick={openWorkspace}
          disabled={creating}
          data-testid="dash-generate-cta"
          className="md:col-span-2 group relative text-left bg-gradient-to-br from-blue-600 to-blue-800 border border-blue-500/30 rounded-xl p-8 overflow-hidden hover:from-blue-500 hover:to-blue-700 transition-all disabled:opacity-70 disabled:cursor-not-allowed h-auto w-full min-w-0"
        >
          <Sparkles className="absolute top-6 right-6 w-6 h-6 text-white/40" />
          <p className="text-xs font-mono text-blue-100 mb-2 uppercase tracking-wider break-words">{t("dashboard.quick_action_label")}</p>
          <h3 className="text-2xl font-semibold text-white mb-2 break-words">{t("dashboard.quick_action_title")}</h3>
          <p className="text-blue-100 text-sm mb-6 max-w-md break-words leading-relaxed">{t("dashboard.quick_action_desc")}</p>
          <span className="inline-flex items-center gap-1.5 text-sm font-mono text-white">
            {creating ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> …</>
            ) : (
              <><Plus className="w-4 h-4" /> {t("dashboard.new_generation")}</>
            )}
          </span>
        </button>
        <Link to="/scripts" data-testid="dash-history-cta"
              className="bg-[#09090B]/90 border border-[#1F2937] backdrop-blur-sm rounded-xl p-8 hover:border-[#2D313F] transition-all h-auto w-full min-w-0">
          <p className="text-xs font-mono text-zinc-500 mb-2 uppercase tracking-wider">{t("dashboard.browse_label")}</p>
          <h3 className="text-xl font-medium text-white mb-2">{t("dashboard.script_history_title")}</h3>
          <p className="text-zinc-400 text-sm">{t("dashboard.script_history_desc")}</p>
        </Link>
      </div>

      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-medium">{t("dashboard.recent_scripts")}</h2>
          <Link to="/scripts" className="text-sm text-zinc-400 hover:text-white" data-testid="dash-view-all">{t("dashboard.view_all")}</Link>
        </div>
        {scripts.length === 0 ? (
          <div className="bg-[#09090B]/90 border border-[#1F2937] backdrop-blur-sm rounded-xl p-12 text-center">
            <p className="text-zinc-500 mb-4">{t("dashboard.empty_scripts")}</p>
            <Button
              onClick={openWorkspace}
              disabled={creating}
              className="bg-blue-600 hover:bg-blue-500 text-white"
              data-testid="empty-generate-btn"
            >
              {creating ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> …</>
              ) : (
                t("dashboard.empty_generate_btn")
              )}
            </Button>
          </div>
        ) : (
          <div className="bg-[#09090B]/90 border border-[#1F2937] backdrop-blur-sm rounded-xl divide-y divide-[#1F2937]">
            {scripts.slice(0, 5).map((s) => (
              <Link key={s.id} to={`/scripts/${s.id}`} data-testid={`recent-script-${s.id}`}
                    className="flex items-center justify-between px-6 py-4 hover:bg-[#12141C]/60 transition-colors">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-400 border border-blue-500/20">{modeLabels[s.mode] || s.mode}</span>
                    <span className="text-xs text-zinc-500 font-mono">{new Date(s.created_at).toLocaleDateString()}</span>
                  </div>
                  <p className="text-white truncate">{s.title}</p>
                </div>
                <ChevronRight className="w-4 h-4 text-zinc-600 flex-shrink-0" />
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * BurnRateTile — Phase 2.1S.
 * Three visual states derived from the /api/users/me/burn_rate payload.
 * Extracted from Dashboard for readability + easier unit testing.
 */
function BurnRateTile({ data, t }) {
  // (0) Loading / fetch-failed — quiet neutral placeholder. No spinner
  // to avoid an anxious UI; a stale value is better than a jittery one.
  if (!data) {
    return (
      <div
        data-testid="dash-burn-rate-card"
        data-state="loading"
        className="bg-[#09090B]/90 border border-[#1F2937] backdrop-blur-sm rounded-xl p-6 min-w-0"
      >
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-mono uppercase tracking-wider text-zinc-500 break-words">
            {t("dashboard.burn_label", { defaultValue: "Verbrauch" })}
          </span>
          <Flame className="w-4 h-4 text-zinc-600 flex-shrink-0" />
        </div>
        <div className="text-3xl font-semibold font-mono tracking-tight text-zinc-500">—</div>
        <p className="text-xs text-zinc-500 mt-2 break-words leading-relaxed">
          {t("dashboard.burn_loading", { defaultValue: "Analyse läuft…" })}
        </p>
      </div>
    );
  }

  const { avg_credits_per_day: avg, days_remaining: days, is_low_runway } = data;

  // (1) Zero-usage state — no debits in the rolling window.
  if (days === null || days === undefined) {
    return (
      <div
        data-testid="dash-burn-rate-card"
        data-state="stable"
        className="bg-[#09090B]/90 border border-[#1F2937] backdrop-blur-sm rounded-xl p-6 min-w-0"
      >
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-mono uppercase tracking-wider text-zinc-500 break-words">
            {t("dashboard.burn_label", { defaultValue: "Verbrauch" })}
          </span>
          <Flame className="w-4 h-4 text-emerald-500 flex-shrink-0" />
        </div>
        <div className="text-xl font-semibold tracking-tight text-emerald-400 break-words">
          {t("dashboard.burn_stable_title", { defaultValue: "Nutzung stabil" })}
        </div>
        <p className="text-xs text-zinc-500 mt-2 break-words leading-relaxed">
          {t("dashboard.burn_stable_desc", { defaultValue: "Keine Verbrauchsspitzen" })}
        </p>
      </div>
    );
  }

  // (2) Low-runway warning — pulsing amber ring + top-up CTA.
  if (is_low_runway) {
    return (
      <div
        data-testid="dash-burn-rate-card"
        data-state="low-runway"
        className="bg-[#09090B]/90 border border-amber-500/40 ring-1 ring-amber-500/20 backdrop-blur-sm rounded-xl p-6 min-w-0 [box-shadow:0_0_0_1px_rgba(245,158,11,0.15),0_8px_24px_-8px_rgba(245,158,11,0.25)]"
      >
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-mono uppercase tracking-wider text-amber-300 break-words">
            {t("dashboard.burn_label", { defaultValue: "Verbrauch" })}
          </span>
          <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0" />
        </div>
        <div
          className="text-xl font-semibold tracking-tight text-amber-300 break-words"
          data-testid="burn-rate-days-remaining"
        >
          {t("dashboard.burn_low_title", {
            days,
            defaultValue: `Achtung: Kredite fast aufgebraucht (noch ${days} Tage)`,
          })}
        </div>
        <p className="text-xs text-zinc-400 mt-2 break-words leading-relaxed">
          Ø {avg} / Tag
        </p>
        <Link
          to="/pricing"
          data-testid="burn-rate-topup-cta"
          className="mt-3 inline-flex items-center gap-1 text-xs font-mono text-amber-300 hover:text-amber-200 transition-colors"
        >
          {t("dashboard.burn_topup_cta", { defaultValue: "Kredite aufladen" })}
          <ChevronRight className="w-3 h-3" />
        </Link>
      </div>
    );
  }

  // (3) Standard state — neutral runway display.
  return (
    <div
      data-testid="dash-burn-rate-card"
      data-state="ok"
      className="bg-[#09090B]/90 border border-[#1F2937] backdrop-blur-sm rounded-xl p-6 min-w-0"
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-mono uppercase tracking-wider text-zinc-500 break-words">
          {t("dashboard.burn_label", { defaultValue: "Verbrauch" })}
        </span>
        <Flame className="w-4 h-4 text-blue-400 flex-shrink-0" />
      </div>
      <div
        className="text-4xl font-semibold font-mono tracking-tight"
        data-testid="burn-rate-days-remaining"
      >
        {days}
      </div>
      <p className="text-xs text-zinc-500 mt-2 break-words leading-relaxed">
        {t("dashboard.burn_ok_footnote", {
          avg,
          defaultValue: `Kredite-Reichweite · ca. ${days} Tage bei Ø ${avg} / Tag`,
        })}
      </p>
    </div>
  );
}

